import type { ContentfulClientApi, EntryFieldTypes } from 'contentful';
import { createClient } from 'contentful';
import { JsonArray, JsonObject } from 'type-fest';
import {
  TypeClimateAction,
  TypePhotoCarousel,
  TypeProductRecommendation,
  TypeClimateActionSkeleton,
  TypePortfolioPageSkeleton,
  TypePortfolioPageFields,
  TypeProjectPageSkeleton,
  TypeFaqSkeleton,
  TypeDonorPortalSkeleton,
  TypeBlogPostSkeleton,
} from './contentfulTypes';

// These types help contentful know that when we fetch actions, we always fetch their
// linked categories and photo carousels, etc.

export type ClimateActionWithoutUnresolvableLinks = TypeClimateAction<
  'WITHOUT_UNRESOLVABLE_LINKS',
  string
>;

export type PhotoCarouselWithoutUnresolvableLinks = TypePhotoCarousel<
  'WITHOUT_UNRESOLVABLE_LINKS',
  string
>;

export type ProductRecommendationWithoutUnresolvableLinks =
  TypeProductRecommendation<'WITHOUT_UNRESOLVABLE_LINKS', string>;

async function graphQL(
  query: string,
  space: string,
  accessToken: string,
  variables: { [key: string]: string } = {}
) {
  const response = await fetch(
    `https://graphql.contentful.com/content/v1/spaces/${space}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    }
  );

  const responseBody = await response.json();

  if (responseBody.errors) {
    throw new Error(responseBody.errors[0].message);
  }

  return responseBody.data;
}

export interface ContentfulActionItemsCollection {
  slug: string;
  actionItemsCollection: {
    items: {
      slug: string;
    }[];
  };
}

export const getActionDetails = async ({
  space,
  accessToken,
}: {
  space: string;
  accessToken: string;
}) => {
  const data = await graphQL(
    `
      query ActionTree {
        climateActionCollection(limit: 500) {
          items {
            slug
            actionItemsCollection(limit: 21) {
              items {
                slug
              }
            }
          }
        }
      }
      `,
    space,
    accessToken
  );

  return data.climateActionCollection
    .items as ContentfulActionItemsCollection[];
};

export const initializeClient = ({
  space,
  accessToken,
}: {
  space: string;
  accessToken: string;
}) => {
  const client = createClient({ space, accessToken });
  return client;
};

export const getActions = async (client: ContentfulClientApi<undefined>) => {
  const data =
    await client.withoutUnresolvableLinks.getEntries<TypeClimateActionSkeleton>(
      {
        content_type: 'climateAction',
        include: 1,
        limit: 1000,
        select: [
          'sys',
          'metadata',
          'fields.name',
          'fields.slug',
          'fields.image',
          'fields.actionCategory',
          'fields.actionType',
          'fields.actionItems',
          'fields.externalLink',
          'fields.countries',
          'fields.startDate',
          'fields.endDate',
          'fields.sunsetMessage',
          'fields.completionText',
          'fields.costDescription',
          'fields.timeDescription',
          'fields.impactDescription',
          'fields.isActionItem',
          'fields.organizationMessage',
          'fields.authors',
          'fields.guides',
          'fields.house',
          'fields.senate',
        ],
      } as any
    );

  return data.items;
};

export const getActionBySlug = async (
  slug: string,
  client: ContentfulClientApi<undefined>
) => {
  const data =
    await client.withoutUnresolvableLinks.getEntries<TypeClimateActionSkeleton>(
      {
        content_type: 'climateAction',
        include: 2,
        limit: 1,
        'fields.slug': slug,
      }
    );

  return data.items[0] ?? null;
};

export const getFaqs = async (client: ContentfulClientApi<undefined>) => {
  const data =
    await client.withoutUnresolvableLinks.getEntries<TypeFaqSkeleton>({
      content_type: 'faq',
      include: 0,
      limit: 1000,
    });

  return data.items;
};

export const getBlogPosts = async (client: ContentfulClientApi<undefined>) => {
  const data =
    await client.withoutUnresolvableLinks.getEntries<TypeBlogPostSkeleton>({
      content_type: 'blogPost',
      include: 4,
      limit: 1000,
    });

  return data.items;
};

export const getBlogPostBySlug = async (
  slug: string,
  client: ContentfulClientApi<undefined>
) => {
  return client.withoutUnresolvableLinks.getEntries<TypeBlogPostSkeleton>({
    content_type: 'blogPost',
    include: 4,
    limit: 1,
    'fields.slug': slug,
  });
};

export interface BlogPostsFilterOptions {
  /** Entry must have all of these tags (slug form, e.g. "project-update"). Matches Contentful "tags" field (Short text, list) with normalized comparison. */
  tagsAll?: string[];
  /** Entry must have at least one of these tags. */
  tagsAny?: string[];
  /** Entry must not have any of these tags. */
  tagsExclude?: string[];
  /** Only return entries where isFeaturedPost is true. Uses Contentful field filter. */
  featuredOnly?: boolean;
  page?: number;
  limit?: number;
}

/**
 * Normalize a tag for comparison: lowercase, spaces to hyphens.
 * Blog post tags are from the custom field "tags" (Short text, list) with human-readable
 * values like "Biochar carbon removal" or "Project update"; filters use slugs like
 * "biochar-carbon-removal", "project-update". This makes them comparable.
 */
function normalizeTag(t: string): string {
  return t.toLowerCase().trim().replace(/\s+/g, '-');
}

/** Return true if entry's tags (normalized) include the requested normalized tag. */
function entryHasTag(
  entryTags: string[],
  requestedNormalized: string
): boolean {
  return entryTags.some((t) => normalizeTag(t) === requestedNormalized);
}

const BLOG_FILTER_BATCH_SIZE = 200;
const BLOG_FILTER_MAX_SCANNED = 1000;

function applyTagFilters<T extends { fields: { tags?: unknown } }>(
  entries: T[],
  normalizedTagsAll: string[],
  tagsAny: string[],
  normalizedTagsExclude: string[],
  needInMemoryTagFilter: boolean
): T[] {
  let items = entries;
  if (normalizedTagsAll.length > 0) {
    items = items.filter((entry) => {
      const entryTags = (entry.fields.tags || []) as string[];
      return normalizedTagsAll.every((req) => entryHasTag(entryTags, req));
    });
  }
  if (tagsAny.length > 0 && needInMemoryTagFilter) {
    const anyNormalized = tagsAny.map((t) => normalizeTag(t));
    items = items.filter((entry) => {
      const entryTags = (entry.fields.tags || []) as string[];
      return anyNormalized.some((tag) => entryHasTag(entryTags, tag));
    });
  }
  if (normalizedTagsExclude.length > 0) {
    items = items.filter((entry) => {
      const tags = (entry.fields.tags || []) as string[];
      return !normalizedTagsExclude.some((ex) =>
        tags.some((t) => normalizeTag(t) === ex)
      );
    });
  }
  return items;
}

export async function getBlogPostsWithFilter(
  client: ContentfulClientApi<undefined>,
  options: BlogPostsFilterOptions = {}
) {
  const {
    tagsAll = [],
    tagsAny = [],
    tagsExclude = [],
    featuredOnly = false,
    page = 1,
    limit = 100,
  } = options;

  const normalizedTagsAll = tagsAll.map((t) => normalizeTag(t));
  const normalizedTagsExclude = tagsExclude.map((t) => normalizeTag(t));

  const needInMemoryTagFilter =
    normalizedTagsAll.length > 0 ||
    normalizedTagsExclude.length > 0 ||
    tagsAny.length > 0;

  if (!needInMemoryTagFilter) {
    const fetchLimit = Math.min(limit, 100);
    const query: Record<string, string | number | boolean> = {
      content_type: 'blogPost',
      include: 4,
      order: '-fields.publishDate',
      limit: fetchLimit,
      skip: (page - 1) * limit,
    };
    if (tagsAny.length > 0) {
      query['fields.tags[in]'] = tagsAny.join(',');
    }
    if (featuredOnly) {
      query['fields.isFeaturedPost'] = true;
    }
    const data =
      await client.withoutUnresolvableLinks.getEntries<TypeBlogPostSkeleton>(
        query as any
      );
    const total = data.total ?? data.items.length;
    const pages = limit > 0 ? Math.ceil(total / limit) : 1;
    return {
      items: data.items,
      pagination: {
        page,
        limit,
        pages,
        total,
        next: page < pages ? page + 1 : null,
        prev: page > 1 ? page - 1 : null,
      },
    };
  }

  const allFiltered: Awaited<
    ReturnType<
      ContentfulClientApi<undefined>['withoutUnresolvableLinks']['getEntries']
    >
  >['items'] = [];
  let skip = 0;
  let totalScanned = 0;

  const batchQuery: Record<string, string | number | boolean> = {
    content_type: 'blogPost',
    include: 4,
    order: '-fields.publishDate',
    limit: BLOG_FILTER_BATCH_SIZE,
  };
  if (featuredOnly) {
    batchQuery['fields.isFeaturedPost'] = true;
  }

  while (totalScanned < BLOG_FILTER_MAX_SCANNED) {
    const batch =
      await client.withoutUnresolvableLinks.getEntries<TypeBlogPostSkeleton>({
        ...batchQuery,
        skip,
      } as any);

    if (batch.items.length === 0) break;

    const filtered = applyTagFilters(
      batch.items,
      normalizedTagsAll,
      tagsAny,
      normalizedTagsExclude,
      needInMemoryTagFilter
    );
    allFiltered.push(...filtered);
    totalScanned += batch.items.length;
    skip += batch.items.length;

    if (batch.items.length < BLOG_FILTER_BATCH_SIZE) break;
  }

  const total = allFiltered.length;
  const start = (page - 1) * limit;
  const items = allFiltered.slice(start, start + limit);
  const pages = limit > 0 ? Math.ceil(total / limit) || 1 : 1;

  return {
    items,
    pagination: {
      page,
      limit,
      pages,
      total,
      next: page < pages ? page + 1 : null,
      prev: page > 1 ? page - 1 : null,
    },
  };
}

export interface PortfolioPageFieldsWithImgix extends TypePortfolioPageFields {
  icon?: EntryFieldTypes.Object<ImgixObject>;
  heroImage?: EntryFieldTypes.Object<ImgixObject>;
}

export interface PortfolioPageSkeletonWithImgix
  extends TypePortfolioPageSkeleton {
  fields: PortfolioPageFieldsWithImgix;
}

export function getPortfolioPage(
  slug: string,
  client: ContentfulClientApi<undefined>
) {
  return client.withoutUnresolvableLinks.getEntries<PortfolioPageSkeletonWithImgix>(
    {
      content_type: 'portfolioPage',
      include: 1,
      limit: 1,
      'fields.slug': slug,
    }
  );
}

export function getProjectPage(
  slug: string,
  client: ContentfulClientApi<undefined>
) {
  return client.withoutUnresolvableLinks.getEntries<TypeProjectPageSkeleton>({
    content_type: 'projectPage',
    include: 2,
    limit: 1,
    'fields.slug': slug,
  });
}

export function getAllProjectPages(client: ContentfulClientApi<undefined>) {
  return client.withoutUnresolvableLinks.getEntries<TypeProjectPageSkeleton>({
    content_type: 'projectPage',
    include: 2,
    limit: 1000,
  });
}

export function getDonorPortalPage(
  portfolioSlug: string,
  client: ContentfulClientApi<undefined>
) {
  return client.withoutUnresolvableLinks.getEntries<TypeDonorPortalSkeleton>({
    content_type: 'donorPortal',
    include: 1,
    limit: 1,
    'fields.portfolioSlug': portfolioSlug,
  });
}

export interface MilestoneEntriesOptions {
  start?: Date;
  end?: Date;
}

/** Aligns with backend getFundingMilestones/getTonnesMilestones month-boundary semantics. */
function startOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

/**
 * First moment of the next month (exclusive upper bound).
 * This matches the DB filtering semantics (monthEnd is exclusive) and avoids
 * millisecond-level edge cases when using Contentful range filters.
 */
function endOfMonthExclusive(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
}

export function getBlogPostEntriesForMilestones(
  client: ContentfulClientApi<undefined>,
  options: MilestoneEntriesOptions = {}
) {
  const { start, end } = options;
  const monthStart = start ? startOfMonth(start) : undefined;
  const monthEndExclusive = end ? endOfMonthExclusive(end) : undefined;
  return client.withoutUnresolvableLinks.getEntries<TypeBlogPostSkeleton>({
    content_type: 'blogPost',
    'fields.milestoneTitle[exists]': 'true',
    include: 4,
    limit: 1000,
    ...(monthStart ? { 'fields.publishDate[gte]': monthStart.toISOString() } : {}),
    ...(monthEndExclusive
      ? { 'fields.publishDate[lt]': monthEndExclusive.toISOString() }
      : {}),
    order: '-fields.publishDate',
  } as never);
}

export interface ImgixObject extends JsonObject {
  attributes: {
    colors?: {
      dominant_colors?: string;
    };
    content_type?: string;
    media_height?: number;
    media_width?: number;
    media_kind?: 'IMAGE' | string;
  };
  src: string;
}

export function isImgixObject(
  object: JsonObject | JsonArray | null | undefined
): object is ImgixObject {
  if (!object || typeof object?.length === 'number') {
    return false;
  }
  return typeof (object as JsonObject).media_kind === 'string';
}

// Returned in descending order of score
export function parseDominantColors(object: ImgixObject): string[] {
  const dominantColorsString = object.attributes.colors?.dominant_colors;
  if (!dominantColorsString) {
    return [];
  }
  const hash = JSON.parse(dominantColorsString);
  const results: { color: string; score: number }[] = [];
  for (const key in hash) {
    results.push({ color: key, score: hash[key] });
  }
  results.sort((a, b) => b.score - a.score);
  return results.map((x) => x.color);
}
